import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { SUPPORTED_LANGUAGES, type Language } from "@/lib/i18n";
import {
  absoluteUrl,
  isPublicPath,
  stripLanguagePrefix,
} from "@/lib/localizedPaths";

const OG_LOCALE: Record<Language, string> = { en: "en_US", "pt-PT": "pt_PT" };

/**
 * Canonical URL, og:url and og:locale for the public page at the current URL, plus
 * hreflang alternates, so each language version points to itself and to the other.
 * Nothing for unknown paths (the not found page).
 */
export function PublicPageAlternates({ language }: { language: Language }) {
  const { pathname } = useLocation();
  if (!isPublicPath(pathname)) return null;

  // "/pricing/" and "/pricing" are the same page.
  const page = stripLanguagePrefix(pathname).replace(/(.)\/+$/, "$1");
  const canonical = absoluteUrl(page, language);

  // Links to the other language versions.
  const alternates = [
    ...SUPPORTED_LANGUAGES.filter((other) => other !== language).map(
      (other) => (
        <meta
          key={`og-${other}`}
          property="og:locale:alternate"
          content={OG_LOCALE[other]}
        />
      ),
    ),
    ...SUPPORTED_LANGUAGES.map((lang) => (
      <link
        key={`hreflang-${lang}`}
        rel="alternate"
        hrefLang={lang}
        href={absoluteUrl(page, lang)}
      />
    )),
    <link
      key="hreflang-default"
      rel="alternate"
      hrefLang="x-default"
      href={absoluteUrl(page, "en")}
    />,
  ];

  return (
    <Helmet>
      <link rel="canonical" href={canonical} />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={OG_LOCALE[language]} />
      {alternates}
    </Helmet>
  );
}
